import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, DatePipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NumberFormatterPipe } from '@coin/shared/core/util-pipes';
import { DynamicDomAccessDirective, EmployeeIdPipe } from '@coin/shared/auth/feature';
import { AuthComponentsModule } from '@coin/shared/auth/feature';
import { RecordAchievementTableComponent } from '@coin/shared/incentive/feature';
import { LoadingModule } from '@coin/shared/core/data-access';
import { FxConversionPipe, FxRatePipe } from '@coin/shared/core/feature-pipes';
import { LegacyComponentsModule } from '@coin/shared/core/feature-legacy-components';
import {
  DropdownSearchDirective,
  V2ButtonComponent,
  V2CardComponent,
  V2CheckboxComponent,
  V2CountryFlagComponent,
  V2DateInputComponent,
  V2DialogModule,
  V2DropdownComponent,
  V2LanguageFlagComponent,
  V2NumberInputComponent,
  V2SearchBoxComponent,
  V2TabsModule,
  V2TextAreaComponent,
  V2TextInputComponent,
  V2ToggleComponent,
  V2TurbineComponent
} from '@coin/shared/core/ui-storybook';
import { AppEnvironment, CustomTranslationLoader } from '@coin/shared/core/data-access-translation-loader';
import { TemplateWithValueDirective, TwoDecimalPlacesDirective } from '@coin/shared/core/util-directives';
import {
  MgmtGroupTranslationPipe,
  PureFunctionPipe,
  SortAlphabeticallyPipe,
  ThousandsSeparatorPipe
} from '@coin/shared/core/util-pipes';
import { TranslateModule } from '@ngx-translate/core';
import { DragScrollComponent, DragScrollItemDirective } from 'ngx-drag-scroll';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { ToastrModule } from 'ngx-toastr';
import { SimplebarAngularModule } from 'simplebar-angular';
import { AddPlanComponent } from './components/add-plan/add-plan.component';
import { AppCardComponent } from './components/app-card/app-card.component';
import { AppCardsComponent } from './components/app-cards/app-cards.component';
import { CalendarIconComponent } from './components/calendar-icon/calendar-icon.component';
import { EditSeasonPlanComponent } from './components/edit-season-plan/edit-season-plan.component';
import { EmployeeTargetOverviewComponent } from './components/employee-target-overview/employee-target-overview.component';
import { NavigationMenuWrapperComponent } from './components/navigation-menu-wrapper/navigation-menu-wrapper.component';
import { NavigationMenuComponent } from './components/navigation-menu/navigation-menu.component';
import { PageHeadlineComponent } from './components/page-headline/page-headline.component';
import { SearchEmployeeDialogComponent } from './components/search-employee-dialog/search-employee-dialog.component';
import { SimpleTableComponent } from './components/simple-table/simple-table.component';
import { TableGroupDirective } from './components/simple-table/table-group.directive';
import { TabSelectionComponent } from './components/tab-selection/tab-selection.component';
import { TableDropdownEditFieldComponent } from './components/table-dropdown-edit-field/table-dropdown-edit-field.component';
import { TagsInputComponent } from './components/tags-input/tags-input.component';
import { ViewInformationDialogComponent } from './components/view-information/parts/view-information-dialog/view-information-dialog.component';
import { ViewInformationComponent } from './components/view-information/view-information.component';
import { DocumentUploadDialogComponent } from './dialogs/document-upload/document-upload-dialog.component';
import { DocumentUploadDialogDirective } from './dialogs/document-upload/document-upload-dialog.directive';
import { LoginComponent } from './dialogs/login/login.component';
import { SelectPlanDialogComponent } from './dialogs/select-plan-dialog/select-plan-dialog.component';
import { CountryCodePipe } from './pipes/country-code.pipe';
import { ImporterBaseService } from './services/importer-base.service';
import { HeadcountSettingsComponent } from './components/headcount-settings/headcount-settings.component';

@NgModule({
  declarations: [
    TabSelectionComponent,
    LoginComponent,
    TagsInputComponent,
    SimpleTableComponent,
    CalendarIconComponent,
    CountryCodePipe,
    EmployeeTargetOverviewComponent,
    TableGroupDirective,
    TableDropdownEditFieldComponent,
    SelectPlanDialogComponent,
    AddPlanComponent,
    EditSeasonPlanComponent,
    DocumentUploadDialogComponent,
    DocumentUploadDialogDirective,
    ViewInformationComponent,
    ViewInformationDialogComponent,
    PageHeadlineComponent,
    NavigationMenuComponent,
    NavigationMenuWrapperComponent,
    AppCardComponent,
    AppCardsComponent,
    SearchEmployeeDialogComponent,
    HeadcountSettingsComponent
  ],
  imports: [
    V2ToggleComponent,
    V2DateInputComponent,
    V2CheckboxComponent,
    DragScrollComponent,
    DragScrollItemDirective,
    DragDropModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatInputModule,
    MatDatepickerModule,
    MatTabsModule,
    MatIconModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatSliderModule,
    MatDialogModule,
    MatCardModule,
    MatPaginatorModule,
    MatMenuModule,
    MatCheckboxModule,
    CommonModule,
    SimplebarAngularModule,
    MatChipsModule,
    MatTooltipModule,
    InfiniteScrollDirective,
    MatProgressBarModule,
    TranslateModule.forChild(CustomTranslationLoader.createConfig(AppEnvironment.Admin)),
    ToastrModule.forRoot(),
    LegacyComponentsModule,
    DynamicDomAccessDirective,
    TwoDecimalPlacesDirective,
    EmployeeIdPipe,
    ThousandsSeparatorPipe,
    MgmtGroupTranslationPipe,
    FxConversionPipe,
    FxRatePipe,
    PureFunctionPipe,
    LoadingModule,
    NumberFormatterPipe,
    V2DialogModule,
    V2TabsModule,
    V2SearchBoxComponent,
    V2ButtonComponent,
    V2TextAreaComponent,
    V2TextInputComponent,
    V2NumberInputComponent,
    TemplateWithValueDirective,
    AuthComponentsModule,
    V2CountryFlagComponent,
    V2CardComponent,
    V2DropdownComponent,
    DropdownSearchDirective,
    V2LanguageFlagComponent,
    V2TurbineComponent,
    SortAlphabeticallyPipe,
    RecordAchievementTableComponent
  ],
  exports: [
    V2ToggleComponent,
    DragScrollComponent,
    DragScrollItemDirective,
    MatDatepickerModule,
    MatSelectModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatTooltipModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    TabSelectionComponent,
    MatTabsModule,
    MatIconModule,
    MatSlideToggleModule,
    LoginComponent,
    MatCardModule,
    TagsInputComponent,
    CountryCodePipe,
    MatPaginatorModule,
    MatMenuModule,
    SimpleTableComponent,
    CalendarIconComponent,
    NumberFormatterPipe,
    EmployeeTargetOverviewComponent,
    TableDropdownEditFieldComponent,
    AddPlanComponent,
    EditSeasonPlanComponent,
    DocumentUploadDialogComponent,
    ViewInformationComponent,
    PageHeadlineComponent,
    NavigationMenuComponent,
    NavigationMenuWrapperComponent,
    AppCardComponent,
    AppCardsComponent
  ],
  providers: [DatePipe, ImporterBaseService]
})
export class SharedModule {}
